// Updated: 2026-09-03
//
// Migrated to React Query in slice 7 (react-query-user-relations, Req 7.5).
//
// THE RETURN SHAPE IS BYTE-IDENTICAL to the pre-migration hook, and that is the point: every pattern card
// and every pattern detail page that calls this hook stays untouched. What moved is where the optimism
// lives — `useToggleFavoritePatternMutation` (`src/queries/userRelations/mutations.ts`) now owns the
// optimistic write, the snapshot and the rollback, so the hook holds no copy of the favorite state at all.
// `favorited` is derived from the ONE cache entry `['me','relations']` through the context's `isFavorited`.
//
// WHAT WENT AWAY: the `addFavorite(patternId)` / `removeFavorite(patternId)` call BEFORE the `await`, the
// inverse pair in `catch`, and `setLoading` in `finally`. The rollback is strictly better in the mutation —
// it restores the whole snapshot instead of reconstructing an inverse patch — and `loading` is now the
// mutation's `isPending`, which cannot get out of step with the request the way a separate `useState` could.

import { useState } from 'react'

import { useAuth } from '../context/AuthContext'
import { useUserRelations } from '../context/UserRelationsContext'
import { useToggleFavoritePatternMutation } from '../queries/userRelations/mutations'

export const useFavoritePattern = (patternId: string) => {
  const [showLoginModal, setShowLoginModal] = useState(false)
  const { idToken } = useAuth()
  const { isFavorited } = useUserRelations()
  const toggle = useToggleFavoritePatternMutation(idToken)

  const favorited = isFavorited(patternId)

  const handleFavoriteClick = (e?: React.MouseEvent) => {
    // FIRST, always. This hook is called from pattern cards nested inside navigating parents, so dropping
    // this line turns a favorite click into a navigation to the pattern page.
    if (e) e.stopPropagation()

    // Stays HERE, short-circuiting before any optimistic write or transport call. It is deliberately not
    // folded into the mutation: a mutation that refused to run would still have to report "show the modal"
    // back out, and the guard is cheaper where it is.
    if (!idToken) {
      setShowLoginModal(true)
      return
    }

    // `nextFavorited` is the INTENT. Passing it (rather than letting the mutation read the current cache)
    // is what keeps the transport choice and the optimistic write derived from the SAME value, so a
    // double-click cannot send `add` while the cache is written as removed.
    toggle.mutate({ patternId, nextFavorited: !favorited })
  }

  /**
   * UN-favorite, unconditionally — the removal leg and never the add leg.
   *
   * Added for the masked reference-surface entry (content-visibility-model task 32, Req 9.6): the owner of a
   * favorites list is offered removal of a reference row whose target has gone `private`.
   *
   * It is NOT `handleFavoriteClick`, and the difference matters. That handler sends `!favorited`, which is
   * derived from `['me','relations']`. On a masked entry that derivation is not trustworthy: the entry the
   * server sent carries only `{ pattern_id, masked }`, and if the relations cache has not loaded (or has
   * dropped the id) `isFavorited` answers false, so a toggle would send ADD and the owner's explicit
   * "remove this" would put the row back. `nextFavorited: false` cannot be misread.
   */
  const handleRemoveFavorite = () => {
    if (!idToken) {
      setShowLoginModal(true)
      return
    }
    toggle.mutate({ patternId, nextFavorited: false })
  }

  return {
    favorited, // isFavorited(patternId) — now derived from the cache
    loading: toggle.isPending, // was a local useState(false) set around the await
    showLoginModal, // unchanged local useState
    setShowLoginModal, // unchanged local setter
    handleFavoriteClick,
    handleRemoveFavorite, // content-visibility-model task 32 — the masked entry's offered removal
  }
}
